/* ==========================================================================
   <seamless-video-loop> — frame-perfect looping for hero/background video
   ==========================================================================

   PROBLEM THIS SOLVES
   -------------------
   A native `<video loop>` restarts by draining the decoder, seeking to 0 and
   decoding a fresh keyframe. For the few hundred milliseconds that takes, the
   element keeps painting its LAST decoded frame, so the loop reads as a frozen
   still. On top of that, brand spots are almost always authored with a static
   head (logo held before the action starts) and a static tail (logo end card
   held after the fade-in). Looped back to back, those two holds merge into one
   long frozen image at every seam — exactly the artefact reported on the AZYA
   hero, which appears "shortly after the logo fade-in, before the video
   restarts".

   HOW IT IS FIXED
   ---------------
   1. Two stacked <video> layers play the same file. While layer A is on
      screen, layer B sits pre-seeked and pre-decoded at the loop-in point, so
      its first frame paints instantly — there is no decode stall to expose.
   2. At the loop-out point layer B is started and cross-dissolved in ON TOP of
      layer A. Only the incoming layer animates its opacity; the outgoing layer
      stays fully opaque underneath until the dissolve finishes. Combined alpha
      is therefore 1 throughout — no brightness dip, and no gap for the poster
      image to show through.
   3. The static head and tail are measured at runtime (#detectMotionRange) and
      excluded from the loop, so the seam always lands on moving footage. The
      logo's fade-IN still plays in full; only the frozen hold after it is
      dissolved away.

   PROGRESSIVE ENHANCEMENT
   -----------------------
   Liquid renders an ordinary `<video autoplay muted loop playsinline>` inside
   this element. With JS disabled, or if anything here fails, that video keeps
   native-looping exactly as it does today. The component only takes over
   (`loop = false`) once the second layer is confirmed pre-rolled, and hands
   control back if autoplay is ever refused.

   AUTHORING ATTRIBUTES (set from blocks/_slide.liquid)
   ---------------------------------------------------
   data-fade        Cross-dissolve length in ms. 0 disables the component.
   data-trim-start  Manual loop-in point in seconds. 0 = use auto-detection.
   data-trim-end    Manual length of the frozen tail in seconds. 0 = auto.
   ========================================================================== */

(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('seamless-video-loop')) return;

  /* Downsample used for motion detection. Small on purpose: the question is
     "did anything move at all", not "what moved", and an 80x45 readback costs
     well under a millisecond. */
  var SAMPLE_W = 80;
  var SAMPLE_H = 45;

  /* Motion scan window and resolution. Brand spots hold their end card for far
     less than six seconds; scanning deeper spends seeks for no gain. */
  var SCAN_SECONDS = 6;
  var SCAN_STEP = 0.2;
  var SCAN_BUDGET_MS = 12000;

  /* Mean per-channel difference (0-255) above which two frames count as
     different. Sits clear of MP4 compression noise on a static frame, which
     measures well under 1 in practice. */
  var STATIC_THRESHOLD = 2;

  /* Safety rails, so a bad detection can never produce a stuttering micro-loop
     or trim away the whole spot. */
  var MIN_LOOP_SECONDS = 2;
  var MAX_TRIM_SECONDS = 6;

  var LAYER_CLASS = 'slide__video-loop__layer';
  var LIVE_CLASS = 'slide__video-loop__layer--live';
  var STANDBY_CLASS = 'slide__video-loop__layer--standby';
  var INCOMING_CLASS = 'slide__video-loop__layer--incoming';
  var IN_CLASS = 'slide__video-loop__layer--in';

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function toNumber(value, fallback) {
    var parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  }

  function whenMetadata(video) {
    if (video.readyState >= 1 && isFinite(video.duration)) return Promise.resolve(video);
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () {
        reject(new Error('metadata timeout'));
      }, SCAN_BUDGET_MS);
      video.addEventListener('loadedmetadata', function () {
        clearTimeout(timer);
        resolve(video);
      }, { once: true });
      video.addEventListener('error', function () {
        clearTimeout(timer);
        reject(new Error('media error'));
      }, { once: true });
    });
  }

  class SeamlessVideoLoop extends HTMLElement {
    connectedCallback() {
      this.fadeMs = toNumber(this.dataset.fade, 700);
      if (this.fadeMs <= 0) return;

      /* Re-entry guard. The Theme Editor detaches and re-attaches sections on
         every settings change, which fires connectedCallback again; rebuilding
         would leak <video> elements and listeners. */
      if (this.built) {
        this.startDriver();
        return;
      }

      this.primary = this.querySelector('video');
      if (!this.primary) return;
      this.built = true;

      this.layers = [this.primary, this.buildStandby()];
      this.activeIndex = 0;
      this.swapping = false;
      this.tookOver = false;

      this.manualStart = clamp(toNumber(this.dataset.trimStart, 0), 0, MAX_TRIM_SECONDS);
      this.manualTail = clamp(toNumber(this.dataset.trimEnd, 0), 0, MAX_TRIM_SECONDS);
      this.loopStart = this.manualStart;
      this.tailSeconds = this.manualTail;

      this.primary.classList.add(LAYER_CLASS, LIVE_CLASS);

      this.hidePosterOnFirstFrame();
      this.startDriver();

      var self = this;
      whenMetadata(this.primary).then(function () {
        /* Pre-roll first, so the component can take over the very first seam
           even on a slow connection. Detection runs in parallel on its own
           probe element and only refines the trim points afterwards. */
        self.preroll();
        if (self.manualStart > 0 && self.manualTail > 0) return;
        self.detectMotionRange().catch(function () {
          /* Tainted canvas, a seek that never settles, a decode error: keep
             whatever the merchant configured and carry on. */
        });
      }).catch(function () {
        /* No metadata at all — leave the native loop in place. */
      });
    }

    disconnectedCallback() {
      this.stopDriver();
      if (this.fadeTimer) clearTimeout(this.fadeTimer);
    }

    /* ---------------------------------------------------------------- layers */

    /* The standby layer is cloned rather than authored in Liquid so that it
       carries the exact same <source> list — same file, same CDN URL, served
       from the HTTP cache — and so a no-JS visitor never downloads it. */
    buildStandby() {
      var standby = this.primary.cloneNode(true);
      standby.removeAttribute('id');
      standby.removeAttribute('autoplay');
      standby.removeAttribute('loop');
      standby.loop = false;
      standby.autoplay = false;
      standby.muted = true;
      standby.defaultMuted = true;
      standby.playsInline = true;
      standby.preload = 'auto';
      standby.setAttribute('aria-hidden', 'true');
      standby.setAttribute('tabindex', '-1');
      standby.classList.add(LAYER_CLASS, STANDBY_CLASS);
      standby.classList.remove(LIVE_CLASS);
      this.appendChild(standby);
      return standby;
    }

    /* The theme paints the video's preview image behind the layers as an LCP
       placeholder. It is frame 0 — on this spot, the logo — so it has to go
       once real frames are on screen, or it would sit under the dissolve. */
    hidePosterOnFirstFrame() {
      var wrapper = this.closest('.slide__img-wrapper') || this.parentElement;
      var poster = wrapper && wrapper.querySelector('.slide__video-poster');
      if (!poster) return;
      var hide = function () {
        poster.classList.add('slide__video-poster--hidden');
      };
      if (this.primary.readyState >= 2) hide();
      this.primary.addEventListener('loadeddata', hide, { once: true });
      this.primary.addEventListener('playing', hide, { once: true });
    }

    /* ------------------------------------------------------------- detection */

    /* Seek-samples the head and the tail of the file and reports the first and
       last timestamps that differ from their neighbour. Everything outside that
       range is a frozen hold and is excluded from the loop.

       This runs on a throwaway probe element rather than on one of the two
       playback layers: seeking a layer would fight with pre-roll and could put
       a half-seeked frame on screen. The probe costs no extra network — it
       resolves to the same cached file — and is removed as soon as it is done. */
    async detectMotionRange() {
      var probe = document.createElement('video');
      Array.prototype.forEach.call(this.primary.querySelectorAll('source'), function (source) {
        probe.appendChild(source.cloneNode(true));
      });
      if (!probe.querySelector('source') && this.primary.currentSrc) probe.src = this.primary.currentSrc;
      probe.muted = true;
      probe.defaultMuted = true;
      probe.playsInline = true;
      probe.preload = 'auto';
      probe.setAttribute('aria-hidden', 'true');
      probe.classList.add('slide__video-loop__probe');
      this.appendChild(probe);

      var deadline = Date.now() + SCAN_BUDGET_MS;
      try {
        await whenMetadata(probe);

        var duration = probe.duration;
        if (!isFinite(duration) || duration < MIN_LOOP_SECONDS) return;

        var canvas = document.createElement('canvas');
        canvas.width = SAMPLE_W;
        canvas.height = SAMPLE_H;
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;

        var seek = function (time) {
          return new Promise(function (resolve, reject) {
            var timer = setTimeout(function () {
              probe.removeEventListener('seeked', done);
              reject(new Error('seek timeout'));
            }, 3000);
            function done() {
              clearTimeout(timer);
              probe.removeEventListener('seeked', done);
              resolve();
            }
            probe.addEventListener('seeked', done);
            probe.currentTime = time;
          });
        };

        var sample = async function (time) {
          await seek(time);
          ctx.drawImage(probe, 0, 0, SAMPLE_W, SAMPLE_H);
          return ctx.getImageData(0, 0, SAMPLE_W, SAMPLE_H).data;
        };

        var delta = function (a, b) {
          var total = 0;
          for (var i = 0; i < a.length; i += 4) {
            total += Math.abs(a[i] - b[i]) + Math.abs(a[i + 1] - b[i + 1]) + Math.abs(a[i + 2] - b[i + 2]);
          }
          return total / (a.length / 4 * 3);
        };

        var headEnd = Math.min(SCAN_SECONDS, duration / 2);
        var times = [];
        var t;
        for (t = 0; t < headEnd; t += SCAN_STEP) times.push(+t.toFixed(3));
        for (t = Math.max(headEnd, duration - SCAN_SECONDS); t < duration - 0.05; t += SCAN_STEP) {
          times.push(+t.toFixed(3));
        }

        var previous = null;
        var motionStart = null;
        var motionEnd = null;
        for (var i = 0; i < times.length; i++) {
          if (Date.now() > deadline) return;
          var frame = await sample(times[i]);
          if (previous && delta(frame, previous) > STATIC_THRESHOLD) {
            if (motionStart === null) motionStart = times[i - 1];
            motionEnd = times[i];
          }
          previous = frame;
        }

        if (motionStart === null || motionEnd === null) return;

        var loopStart = clamp(motionStart, 0, MAX_TRIM_SECONDS);
        var tail = clamp(duration - motionEnd, 0, MAX_TRIM_SECONDS);
        if (duration - tail - loopStart < MIN_LOOP_SECONDS) return;

        /* A value the merchant set by hand always wins over detection. */
        if (this.manualStart <= 0) this.loopStart = loopStart;
        if (this.manualTail <= 0) this.tailSeconds = tail;
        this.repark();
      } finally {
        probe.removeAttribute('src');
        probe.load();
        probe.remove();
      }
    }

    /* --------------------------------------------------------------- preroll */

    /* Parks the standby layer on the loop-in frame and waits for it to be
       decoded. Only then is it safe to switch off native looping: from that
       point the component owns the seam. */
    preroll() {
      var standby = this.layers[1 - this.activeIndex];
      var self = this;
      var settled = false;
      var ready = function () {
        if (settled) return;
        settled = true;
        self.tookOver = true;
        self.layers[self.activeIndex].loop = false;
      };

      standby.addEventListener('seeked', function handler() {
        standby.removeEventListener('seeked', handler);
        if (standby.readyState >= 2) ready();
        else standby.addEventListener('loadeddata', ready, { once: true });
      });

      whenMetadata(standby).then(function () {
        if (standby.readyState >= 2 && Math.abs(standby.currentTime - self.loopStart) < 0.05) {
          ready();
          return;
        }
        standby.currentTime = self.loopStart;
        /* A seek to the frame the element is already parked on can be optimised
           away without ever firing `seeked`. Don't let takeover hang on it. */
        setTimeout(function () {
          if (standby.readyState >= 2) ready();
        }, 1200);
      }).catch(function () {});
    }

    /* Detection runs after the standby layer has already been parked, so once
       it lands on a better loop-in point the layer has to be moved to it —
       otherwise the first swap would seek on screen and stall exactly where the
       component is supposed to be seamless. */
    repark() {
      var standby = this.layers[1 - this.activeIndex];
      if (this.swapping) return;
      if (Math.abs(standby.currentTime - this.loopStart) < 0.05) return;
      try {
        standby.currentTime = this.loopStart;
      } catch (error) {
        /* Unbuffered range; swap() seeks again anyway. */
      }
    }

    /* ---------------------------------------------------------------- driver */

    /* requestVideoFrameCallback fires once per presented frame, the most
       accurate clock available for hitting the seam. timeupdate (~4Hz) runs in
       parallel as a backup: rVFC is throttled hard in background tabs and is
       still missing on some browsers. */
    startDriver() {
      if (this.driving) return;
      this.driving = true;
      var self = this;

      this.onTimeUpdate = function () {
        self.check();
      };
      this.layers.forEach(function (layer) {
        layer.addEventListener('timeupdate', self.onTimeUpdate);
      });

      this.tick = function () {
        if (!self.driving) return;
        self.check();
        self.schedule();
      };
      this.schedule();
    }

    /* Always re-reads the active layer, so the chain follows the swap instead
       of dying on the layer that was just paused. */
    schedule() {
      var active = this.layers[this.activeIndex];
      if (active.requestVideoFrameCallback) this.rvfc = active.requestVideoFrameCallback(this.tick);
      else this.raf = requestAnimationFrame(this.tick);
    }

    stopDriver() {
      this.driving = false;
      if (this.raf) cancelAnimationFrame(this.raf);
      var self = this;
      if (this.onTimeUpdate && this.layers) {
        this.layers.forEach(function (layer) {
          layer.removeEventListener('timeupdate', self.onTimeUpdate);
        });
      }
    }

    check() {
      if (!this.tookOver || this.swapping) return;
      var active = this.layers[this.activeIndex];
      var duration = active.duration;
      if (!isFinite(duration)) return;

      var fadeSeconds = this.fadeMs / 1000;

      /* Start the dissolve where the motion stops, so the closing fade-in is
         seen in full and the frozen hold behind it is what gets covered. If the
         tail is shorter than the dissolve, pull the start back just far enough
         to keep the whole dissolve inside the file. */
      var fadeStart = Math.max(0, Math.min(duration - this.tailSeconds, duration - fadeSeconds));
      if (active.currentTime >= fadeStart) this.swap();
    }

    /* ------------------------------------------------------------------ swap */

    swap() {
      this.swapping = true;

      var outgoing = this.layers[this.activeIndex];
      var incoming = this.layers[1 - this.activeIndex];
      var self = this;

      if (Math.abs(incoming.currentTime - this.loopStart) > 0.05) incoming.currentTime = this.loopStart;
      incoming.classList.remove(STANDBY_CLASS);
      incoming.classList.add(INCOMING_CLASS);

      var playback = incoming.play();
      if (playback && playback.catch) {
        playback.catch(function () {
          /* Autoplay refused — iOS Low Power Mode is the usual cause. Undo the
             swap and hand the seam back to the browser's own loop. */
          if (self.fadeTimer) clearTimeout(self.fadeTimer);
          incoming.classList.remove(INCOMING_CLASS, IN_CLASS);
          incoming.classList.add(STANDBY_CLASS);
          outgoing.loop = true;
          self.tookOver = false;
          self.swapping = false;
        });
      }

      /* Force a style flush so the 0 -> 1 opacity change animates instead of
         collapsing into the same frame as the class that set it to 0. */
      void incoming.offsetWidth;
      incoming.classList.add(IN_CLASS);

      this.fadeTimer = setTimeout(function () {
        if (!self.swapping) return;

        outgoing.classList.add(STANDBY_CLASS);
        outgoing.classList.remove(LIVE_CLASS);
        outgoing.pause();

        incoming.classList.remove(INCOMING_CLASS, IN_CLASS);
        incoming.classList.add(LIVE_CLASS);

        self.activeIndex = 1 - self.activeIndex;
        self.swapping = false;

        /* Park the layer that just left screen on the loop-in frame, so it is
           decoded and ready long before the next seam. */
        try {
          outgoing.currentTime = self.loopStart;
        } catch (error) {
          /* Seeking an unbuffered range throws on some browsers; the next swap
             sets currentTime again anyway. */
        }

        /* rVFC was chained to the layer that just paused — restart on the new
           active layer. */
        self.schedule();
      }, this.fadeMs);
    }
  }

  window.customElements.define('seamless-video-loop', SeamlessVideoLoop);
})();
